import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LiaCoinsSolid } from 'react-icons/lia';
import { useProfile } from '../../contexts/ProfileContext';
import { COIN_PACKS } from '../../economy/economyCatalog';
import { createCheckoutSession } from '../../utils/coinShopApi';
import {
  getPaymentMethods,
  createSetupSession,
  deletePaymentMethod,
  buyPackWithSavedCard,
} from '../../utils/paymentMethodsApi';
import SavedCardSection from './SavedCardSection';
import './CoinShopPage.css';

const formatUsd = (cents) => {
  const dollars = (Number(cents) || 0) / 100;
  return dollars.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
};

const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

const CoinShopPage = () => {
  const { user, coins, refreshCoins } = useProfile();
  const query = useQuery();

  // ── UI state ──────────────────────────────────────────────────────
  const [status, setStatus] = useState(null); // 'success' | 'cancel' | 'error' | 'card_added' | 'card_canceled'
  const [errorMessage, setErrorMessage] = useState('');
  const [busyPackId, setBusyPackId] = useState(null);

  // ── Saved card state ──────────────────────────────────────────────
  const [savedCard, setSavedCard] = useState(null);    // first saved card or null
  const [cardLoading, setCardLoading] = useState(false);
  const [cardBusy, setCardBusy] = useState(false);     // add / remove in progress

  // ── Fetch saved cards on mount (when user is logged in) ───────────
  const fetchSavedCard = useCallback(async () => {
    if (!user) {
      setSavedCard(null);
      return;
    }
    setCardLoading(true);
    try {
      const data = await getPaymentMethods();
      const methods = data?.paymentMethods || [];
      setSavedCard(methods.length > 0 ? methods[0] : null);
    } catch (err) {
      console.error('[CoinShop] fetch payment methods failed', err);
      setSavedCard(null);
    } finally {
      setCardLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchSavedCard();
  }, [fetchSavedCard]);

  // ── Handle query-string status from redirects ─────────────────────
  useEffect(() => {
    const isSuccess = query.get('success') === '1';
    const isCanceled = query.get('canceled') === '1';
    const isCardAdded = query.get('card_added') === '1';
    const isCardCanceled = query.get('card_canceled') === '1';

    if (isSuccess) {
      setStatus('success');
      refreshCoins?.();
      fetchSavedCard(); // card may have been saved during checkout
    } else if (isCanceled) {
      setStatus('cancel');
    } else if (isCardAdded) {
      setStatus('card_added');
      fetchSavedCard(); // reload the new card
    } else if (isCardCanceled) {
      setStatus('card_canceled');
    }
  }, [query, refreshCoins, fetchSavedCard]);

  // ── Add card (redirect to Stripe setup) ───────────────────────────
  const onAddCard = async () => {
    setCardBusy(true);
    setStatus(null);
    setErrorMessage('');
    try {
      const data = await createSetupSession();
      if (!data?.url) throw new Error('Missing setup URL');
      window.location.assign(data.url);
    } catch (err) {
      console.error('[CoinShop] create setup session failed', err);
      setStatus('error');
      setErrorMessage(err?.message || 'Failed to start card setup');
      setCardBusy(false);
    }
  };

  // ── Remove card ───────────────────────────────────────────────────
  const onRemoveCard = async (paymentMethodId) => {
    setCardBusy(true);
    setStatus(null);
    setErrorMessage('');
    try {
      await deletePaymentMethod(paymentMethodId);
      setSavedCard(null);
    } catch (err) {
      console.error('[CoinShop] delete payment method failed', err);
      setStatus('error');
      setErrorMessage(err?.message || 'Failed to remove card');
    } finally {
      setCardBusy(false);
    }
  };

  // ── Buy a pack ────────────────────────────────────────────────────
  const onBuy = async (packId) => {
    if (!user) return;
    if (busyPackId) return;
    setBusyPackId(packId);
    setStatus(null);
    setErrorMessage('');

    try {
      // ── 1-click path: use saved card ──
      if (savedCard) {
        const result = await buyPackWithSavedCard({
          packId,
          paymentMethodId: savedCard.id,
        });

        if (result?.status === 'succeeded') {
          setStatus('success');
          // Give the webhook a moment to credit coins, then refresh.
          setTimeout(() => refreshCoins?.(), 1500);
          setBusyPackId(null);
          return;
        }

        if (result?.status === 'requires_action' && result?.url) {
          // 3DS required → redirect to Stripe (card pre-filled).
          window.location.assign(result.url);
          return; // page will navigate away
        }

        throw new Error('Unexpected response from server');
      }

      // ── Fallback: no saved card → normal Checkout redirect ──
      const data = await createCheckoutSession({ packId });
      if (!data?.url) throw new Error('Missing checkout URL');
      window.location.assign(data.url);
    } catch (err) {
      console.error('[CoinShop] buy failed', err);
      setStatus('error');
      setErrorMessage(err?.message || 'Failed to start checkout');
      setBusyPackId(null);
    }
  };

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div className="coin-shop">
      <div className="coin-shop-header">
        <div className="coin-shop-titleblock">
          <h2>Coin Shop</h2>
        </div>
        <div className="coin-shop-balance">
          <span className="coin-shop-balance-label">Balance</span>
          <span className="coin-shop-balance-value">
            <LiaCoinsSolid className="coin-shop-coin-icon" aria-hidden="true" focusable="false" />
            <span className="coin-shop-balance-number">{typeof coins === 'number' ? coins : 0}</span>
          </span>
        </div>
      </div>

      {!user ? (
        <div className="coin-shop-callout">
          <div className="coin-shop-callout-title">Sign in to buy coins</div>
          <div className="coin-shop-callout-body">
            Purchases are linked to your account so your coins stay with you.
          </div>
          <Link to="/auth" className="coin-shop-link">
            Go to Account
          </Link>
        </div>
      ) : (
        <>
          {/* ── Status alerts ─────────────────────────────────── */}
          {status === 'success' && (
            <div className="coin-shop-alert success">
              Payment received. Your coins will appear shortly (usually instantly). Refresh the page if necessary.
            </div>
          )}
          {status === 'cancel' && (
            <div className="coin-shop-alert neutral">Checkout canceled.</div>
          )}
          {status === 'card_added' && (
            <div className="coin-shop-alert success">
              Card saved! You can now buy coin packs with one click.
            </div>
          )}
          {status === 'card_canceled' && (
            <div className="coin-shop-alert neutral">Card setup canceled.</div>
          )}
          {status === 'error' && (
            <div className="coin-shop-alert error">{errorMessage || 'Something went wrong.'}</div>
          )}

          {/* ── Saved card section ────────────────────────────── */}
          {!cardLoading && (
            <SavedCardSection
              card={savedCard}
              onAddCard={onAddCard}
              onRemoveCard={onRemoveCard}
              busy={cardBusy}
            />
          )}

          {/* ── Coin packs grid ───────────────────────────────── */}
          <div className="coin-shop-grid">
            {COIN_PACKS.map((pack) => {
              const isBusy = busyPackId === pack.id;
              const bonusPct =
                typeof pack.bonusPct === 'number' && pack.bonusPct > 0 ? pack.bonusPct : 0;
              return (
                <div
                  key={pack.id}
                  className={`coin-pack ${pack.featured ? 'featured' : ''}`}
                >
                  {pack.featured && <div className="coin-pack-badge">Most popular</div>}
                  <div className="coin-pack-title">{pack.label}</div>
                  <div className="coin-pack-coins">
                    <LiaCoinsSolid className="coin-shop-coin-icon" aria-hidden="true" focusable="false" />
                    <span className="coin-pack-coins-number">{pack.coins.toLocaleString()}</span>
                  </div>
                  <div className="coin-pack-price">{formatUsd(pack.price.amountCents)}</div>
                  {bonusPct > 0 && (
                    <div className="coin-pack-bonus">{`Includes +${Math.round(
                      bonusPct * 100
                    )}% bonus`}</div>
                  )}
                  <button
                    className="coin-pack-buy"
                    type="button"
                    onClick={() => onBuy(pack.id)}
                    disabled={Boolean(busyPackId)}
                  >
                    {isBusy
                      ? 'Processing...'
                      : savedCard
                        ? 'Buy now'
                        : 'Buy'}
                  </button>
                  {process.env.NODE_ENV !== 'production' && !pack.stripePriceId && (
                    <div className="coin-pack-note">
                      Admin note: no Stripe price id set (fallback pricing will be used).
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default CoinShopPage;
